import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Paper,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  Tooltip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Stack,
  Avatar,
  Divider,
  LinearProgress,
  Alert,
  Zoom,
  Fade,
  Slide,
  CircularProgress,
  Badge,
  ButtonGroup,
  FormLabel,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Switch,
  Tabs,
  Tab
} from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';
import Search from '@mui/icons-material/Search';
import {
  AccessTime as AccessTimeIcon,
  MonetizationOn as MonetizationOnIcon,
  People as PeopleIcon,
  WorkHistory as WorkHistoryIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Speed as SpeedIcon,
  PersonAdd as PersonAddIcon,
  Visibility as ViewIcon,
  Assessment as AssessmentIcon,
  Shop as ShopIcon,
  MusicNote as TikTokIcon,
  AllInclusive as MultiIcon
} from '@mui/icons-material';
import UserEarningsHistory from './UserEarningsHistory';
import CommissionConfigEditor from './CommissionConfigEditor';
import LivePolicyEditor from './LivePolicyEditor';
import { adminSavePeriodSummary } from '../../services/livestreamService';
import { getSystemSetting } from '../../services/systemSettingsService';
import axios from 'axios';
import { getUserData } from '../../services/storageService';

const modernTheme = {
  colors: {
    primary: {
      main: '#2563eb',
      dark: '#1e40af',
      light: '#3b82f6',
      gradient: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
    },
    secondary: {
      main: '#8b5cf6',
      dark: '#7c3aed',
      light: '#a78bfa',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)'
    },
    success: {
      main: '#10b981',
      dark: '#059669',
      light: '#34d399',
      gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
    },
    warning: {
      main: '#f59e0b',
      dark: '#d97706',
      light: '#fbbf24',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
    },
    error: {
      main: '#dc2626',
      dark: '#b91c1c',
      light: '#ef4444',
      gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
    },
    info: {
      main: '#0891b2',
      dark: '#0e7490',
      light: '#22d3ee',
      gradient: 'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)'
    },
    shopee: {
      main: '#EE4D2D',
      gradient: 'linear-gradient(135deg, #EE4D2D 0%, #FF6B3D 100%)'
    },
    tiktok: {
      main: '#000000',
      gradient: 'linear-gradient(135deg, #000000 0%, #333333 100%)'
    },
    multi: {
      main: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)'
    }
  },
  gradients: {
    main: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    card: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
    glass: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    stats: [
      'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',  // Blue
      'linear-gradient(135deg, #10b981 0%, #059669 100%)',  // Green
      'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',  // Orange
      'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'   // Purple
    ]
  },
  shadows: {
    card: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    elevated: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    soft: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
  }
};

const UserManagement = ({
  users = [],
  userSummaries = [],
  showNotification,
  createUser,
  updateUser,
  deleteUser,
  fetchData,
  isMobile
}) => {
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    fullName: '',
    role: 'user',
    departmentRoles: [],
    hourlyRate: 100000,
    kpiHoursPerPeriod: 160,
    commissionPercent: 0,
    baseSalary: 0,
    allowedPlatforms: [],
    platformConfigs: {
      shopee: { isActive: false, hourlyRate: 50000, kpiHoursPerPeriod: 160 },
      tiktok: { isActive: false, hourlyRate: 45000, kpiHoursPerPeriod: 140 }
    },
    allowedTimeSlots: [],
    leaderConfig: null
  });

  const [openEarningsHistory, setOpenEarningsHistory] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
  const [openCommissionConfig, setOpenCommissionConfig] = useState(false);
  const [selectedUserForCommission, setSelectedUserForCommission] = useState(null);
  const [openLivePolicy, setOpenLivePolicy] = useState(false);
  const [selectedUserForPolicy, setSelectedUserForPolicy] = useState(null);
  const [savingPeriodSummary, setSavingPeriodSummary] = useState(false);
  const [openPeriodDialog, setOpenPeriodDialog] = useState(false);
  const [dialogSlide, setDialogSlide] = useState(false);

  // Time slot management state
  const [newTimeSlot, setNewTimeSlot] = useState({
    dayOfWeek: 1, // Monday
    startTime: '07:00',
    endTime: '10:00',
    platform: 'both'
  });

  // System time slots (available time slots from admin)
  const [systemTimeSlots, setSystemTimeSlots] = useState([]);

  // Search + filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [dialogTab, setDialogTab] = useState(0);

  // Commission data
  const [commissionMap, setCommissionMap] = useState({});

  const fetchCommissionData = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';
      const user = getUserData();
      const token = user?.userId || user?.token;
      const res = await axios.get(`${API_URL}/commission/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        const map = {};
        (res.data.data?.users || []).forEach(c => {
          map[c.userId?.toString()] = {
            commission: c.commissionAmount || 0,
            leaderBonus: c.leaderBonus || 0,
            totalGMV: c.totalGMV || 0,
          };
        });
        setCommissionMap(map);
      }
    } catch (e) {
      console.log('Commission fetch error:', e.message);
    }
  }, []);

  useEffect(() => { fetchCommissionData(); }, [fetchCommissionData]);

  const getUserCommission = (userId) => {
    const data = commissionMap[userId?.toString()];
    return {
      commission: data?.commission || 0,
      leaderBonus: data?.leaderBonus || 0,
      totalGMV: data?.totalGMV || 0,
      total: (data?.commission || 0) + (data?.leaderBonus || 0),
    };
  };

  // Memoize để tránh tính toán lại khi component render
  const userSummaryMap = useMemo(() => {
    const map = {};
    if (Array.isArray(userSummaries)) {
      userSummaries.forEach(summary => {
        const userId = typeof summary.userId === 'object' ? summary.userId._id : summary.userId;
        map[userId] = summary;
      });
    }
    return map;
  }, [userSummaries]);

  // Filtered users memo
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchName = !searchQuery || u.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) || u.username?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchRole = !roleFilter || u.role === roleFilter;
      return matchName && matchRole;
    });
  }, [users, searchQuery, roleFilter]);

  // Helper functions for platform display
  const getRoleDisplayInfo = (role, allowedPlatforms = [], departmentRoles = []) => {
    const deptInfo = getDepartmentInfo(departmentRoles);

    switch (role) {
      case 'admin':
        return {
          text: 'Quản trị viên',
          icon: '👑',
          color: modernTheme.colors.warning.main,
          platforms: 'Tất cả platforms',
          department: 'Tất cả phòng ban'
        };
      case 'marketing_staff':
        return {
          text: 'Nhân viên Marketing',
          icon: '📊',
          color: modernTheme.colors.secondary.main,
          platforms: 'Không livestream',
          department: deptInfo
        };
      case 'live_shopee':
        return {
          text: 'Live Shopee',
          icon: '🛒',
          color: modernTheme.colors.shopee.main,
          platforms: 'Shopee',
          department: deptInfo
        };
      case 'live_tiktok':
        return {
          text: 'Live TikTok',
          icon: '🎵',
          color: modernTheme.colors.tiktok.main,
          platforms: 'TikTok',
          department: deptInfo
        };
      case 'live_multi':
        return {
          text: 'Multi Platform',
          icon: '🎭',
          color: modernTheme.colors.multi.main,
          platforms: allowedPlatforms?.join(', ') || 'Chưa cấu hình',
          department: deptInfo
        };
      case 'leader_live':
        return {
          text: 'Lead Team Live',
          icon: '👑',
          color: '#7c3aed',
          platforms: 'Shopee + TikTok',
          department: '📺 LiveStream'
        };
      default:
        return {
          text: 'Nhân viên',
          icon: '👤',
          color: modernTheme.colors.info.main,
          platforms: 'Không có quyền livestream',
          department: deptInfo
        };
    }
  };

  // Helper function để lấy thông tin department
  const getDepartmentInfo = (departmentRoles = []) => {
    if (!departmentRoles || departmentRoles.length === 0) {
      return 'Chưa gán phòng ban';
    }

    const deptMap = {
      'marketing_dept': '📊 Marketing',
      'livestream_dept': '📺 LiveStream'
    };

    const deptNames = departmentRoles.map(dept => deptMap[dept] || dept).join(', ');
    return deptNames;
  };

  // Handle department roles change
  const handleDepartmentRolesChange = (department) => {
    setUserForm(prev => {
      const currentDeptRoles = prev.departmentRoles || [];
      const newDeptRoles = currentDeptRoles.includes(department)
        ? currentDeptRoles.filter(d => d !== department)
        : [...currentDeptRoles, department];

      return {
        ...prev,
        departmentRoles: newDeptRoles
      };
    });
  };

  // Định dạng tiền Việt Nam
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  // Lấy thông tin summary của user
  const getUserSummary = (userId) => {
    return userSummaryMap[userId] || { totalHours: 0, totalEarnings: 0, periodStart: null, periodEnd: null };
  };

  // Tính toán thống kê tổng quan
  const calculateStats = () => {
    const totalUsers = users.length;
    const activeUsers = userSummaries.filter(user => user.totalHours > 0).length;
    const totalHours = userSummaries.reduce((sum, user) => sum + (user.totalHours || 0), 0);
    const totalEarnings = userSummaries.reduce((sum, user) => sum + (user.totalEarnings || 0), 0);
    const avgPerformance = users.length > 0 ?
      users.reduce((sum, user) => {
        const summary = getUserSummary(user._id);
        const performance = Math.min(100, (summary.totalHours / (user.kpiHoursPerPeriod || 160)) * 100);
        return sum + performance;
      }, 0) / users.length : 0;

    return {
      totalUsers,
      activeUsers,
      totalHours,
      totalEarnings,
      avgPerformance: Math.round(avgPerformance)
    };
  };

  const stats = calculateStats();

  // Hàm mở dialog lịch sử thu nhập
  const handleOpenEarningsHistory = (user) => {
    setSelectedUserForHistory(user);
    setOpenEarningsHistory(true);
  };

  const handleCloseEarningsHistory = () => {
    setOpenEarningsHistory(false);
    setSelectedUserForHistory(null);
  };

  // Hàm lưu tổng kết kỳ thủ công
  const handleSavePeriodSummary = async () => {
    setOpenPeriodDialog(true);
    setDialogSlide(true);
  };

  const handleConfirmSavePeriodSummary = async (periodType) => {
    setOpenPeriodDialog(false);
    setDialogSlide(false);

    const periodTypeText = periodType === 'current' ? 'kỳ hiện tại (đang diễn ra)' : 'kỳ vừa kết thúc';
    if (window.confirm(`Bạn có chắc chắn muốn lưu tổng kết ${periodTypeText}?`)) {
      setSavingPeriodSummary(true);
      try {
        const result = await adminSavePeriodSummary(periodType);
        if (result.success) {
          showNotification(result.message || 'Đã lưu tổng kết kỳ thành công');
          fetchData();
        } else {
          showNotification(result.error?.message || 'Không thể lưu tổng kết kỳ', 'error');
        }
      } catch (error) {
        console.error('Error saving period summary:', error);
        showNotification('Có lỗi xảy ra khi lưu tổng kết kỳ', 'error');
      } finally {
        setSavingPeriodSummary(false);
      }
    }
  };

  // Helper function to get day name in Vietnamese
  const getDayName = (dayOfWeek) => {
    const days = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
    return days[dayOfWeek];
  };

  // Handler để thêm time slot
  const handleAddTimeSlot = () => {
    setUserForm(prev => ({
      ...prev,
      allowedTimeSlots: [...(prev.allowedTimeSlots || []), { ...newTimeSlot }]
    }));
    // Reset form
    setNewTimeSlot({
      dayOfWeek: 1,
      startTime: '07:00',
      endTime: '10:00',
      platform: 'both'
    });
  };

  // Handler để xóa time slot
  const handleRemoveTimeSlot = (index) => {
    setUserForm(prev => ({
      ...prev,
      allowedTimeSlots: prev.allowedTimeSlots.filter((_, i) => i !== index)
    }));
  };

  // Load system time slots from database
  const loadSystemTimeSlots = async () => {
    try {
      const result = await getSystemSetting('customTimeSlots');
      if (result.success && result.data) {
        setSystemTimeSlots(result.data);
      }
    } catch (error) {
      console.error('Error loading system time slots:', error);
    }
  };

  // Hàm mở dialog thêm/sửa user
  const handleOpenUserDialog = (userToEdit = null) => {
    // Load system time slots when opening dialog
    loadSystemTimeSlots();
    setDialogTab(0);

    if (userToEdit) {
      setSelectedUserForEdit(userToEdit);
      setUserForm({
        username: userToEdit.username,
        password: '',
        fullName: userToEdit.fullName,
        role: userToEdit.role,
        departmentRoles: userToEdit.departmentRoles || [],
        hourlyRate: userToEdit.hourlyRate ?? 100000,
        kpiHoursPerPeriod: userToEdit.kpiHoursPerPeriod ?? 160,
        commissionPercent: userToEdit.commissionPercent ?? 0,
        baseSalary: userToEdit.baseSalary ?? 0,
        allowedPlatforms: userToEdit.allowedPlatforms || [],
        platformConfigs: userToEdit.platformConfigs || {
          shopee: { isActive: false, hourlyRate: 50000, kpiHoursPerPeriod: 160 },
          tiktok: { isActive: false, hourlyRate: 45000, kpiHoursPerPeriod: 140 }
        },
        allowedTimeSlots: userToEdit.allowedTimeSlots || [],
        leaderConfig: userToEdit.leaderConfig || null
      });
    } else {
      setSelectedUserForEdit(null);
      setUserForm({
        username: '',
        password: '',
        fullName: '',
        role: 'user',
        departmentRoles: [],
        hourlyRate: 100000,
        kpiHoursPerPeriod: 160,
        commissionPercent: 0,
        baseSalary: 0,
        allowedPlatforms: [],
        platformConfigs: {
          shopee: { isActive: false, hourlyRate: 50000, kpiHoursPerPeriod: 160 },
          tiktok: { isActive: false, hourlyRate: 45000, kpiHoursPerPeriod: 140 }
        },
        allowedTimeSlots: [],
        leaderConfig: null
      });
    }
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setSelectedUserForEdit(null);
    setUserForm({
      username: '',
      password: '',
      fullName: '',
      role: 'user',
      departmentRoles: [],
      hourlyRate: 100000,
      kpiHoursPerPeriod: 160,
      commissionPercent: 0,
      baseSalary: 0,
      allowedPlatforms: [],
      platformConfigs: {
        shopee: { isActive: false, hourlyRate: 50000, kpiHoursPerPeriod: 160 },
        tiktok: { isActive: false, hourlyRate: 45000, kpiHoursPerPeriod: 140 }
      },
      allowedTimeSlots: [],
      leaderConfig: null
    });
    // Reset new time slot form
    setNewTimeSlot({
      dayOfWeek: 1,
      startTime: '07:00',
      endTime: '10:00',
      platform: 'both'
    });
  };

  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => ({
      ...prev,
      [name]: name === 'hourlyRate' || name === 'kpiHoursPerPeriod' || name === 'baseSalary' ? parseInt(value, 10) || 0
        : name === 'commissionPercent' ? parseFloat(value) || 0
        : value
    }));
  };

  // Handle role change và platform config updates
  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setUserForm(prev => {
      const updatedForm = { ...prev, role: newRole };

      // Auto-assign department roles based on role
      if (newRole === 'live_shopee' || newRole === 'live_tiktok' || newRole === 'live_multi' || newRole === 'leader_live') {
        // Livestream roles automatically get livestream_dept
        updatedForm.departmentRoles = ['livestream_dept'];
      } else if (newRole === 'admin') {
        // Admin gets both departments
        updatedForm.departmentRoles = ['marketing_dept', 'livestream_dept'];
      } else if (newRole === 'marketing_staff') {
        // Marketing staff gets marketing_dept
        updatedForm.departmentRoles = ['marketing_dept'];
      } else {
        // Regular user - keep current departmentRoles or empty
        updatedForm.departmentRoles = prev.departmentRoles || [];
      }

      // Reset platform configs based on role
      if (newRole === 'live_shopee') {
        updatedForm.allowedPlatforms = [];
        updatedForm.platformConfigs = {
          ...prev.platformConfigs,
          shopee: { ...prev.platformConfigs.shopee, isActive: true },
          tiktok: { ...prev.platformConfigs.tiktok, isActive: false }
        };
      } else if (newRole === 'live_tiktok') {
        updatedForm.allowedPlatforms = [];
        updatedForm.platformConfigs = {
          ...prev.platformConfigs,
          shopee: { ...prev.platformConfigs.shopee, isActive: false },
          tiktok: { ...prev.platformConfigs.tiktok, isActive: true }
        };
      } else if (newRole === 'admin') {
        updatedForm.allowedPlatforms = [];
        updatedForm.platformConfigs = {
          ...prev.platformConfigs,
          shopee: { ...prev.platformConfigs.shopee, isActive: true },
          tiktok: { ...prev.platformConfigs.tiktok, isActive: true }
        };
      } else if (newRole === 'live_multi') {
        // Keep current allowedPlatforms hoặc default
        updatedForm.allowedPlatforms = prev.allowedPlatforms.length > 0 ? prev.allowedPlatforms : ['shopee'];
      } else if (newRole === 'leader_live') {
        updatedForm.allowedPlatforms = ['shopee', 'tiktok'];
        updatedForm.platformConfigs = {
          ...prev.platformConfigs,
          shopee: { ...prev.platformConfigs.shopee, isActive: true },
          tiktok: { ...prev.platformConfigs.tiktok, isActive: true }
        };
        // Initialize leaderConfig with defaults if not present
        if (!updatedForm.leaderConfig) {
          updatedForm.leaderConfig = {
            // 0,4% theo chính sách hiệu lực 01/08/2026
            teamCommissionPercent: 0.4,
            growthBonusPercent: 2,
            tiktokKpiBonusPercent: 5,
            tiktokGrowthThreshold: 20,
            minLiveHoursPerMonth: 90,
            teamMemberIds: []
          };
        }
      } else if (newRole === 'marketing_staff') {
        updatedForm.allowedPlatforms = [];
        updatedForm.platformConfigs = {
          ...prev.platformConfigs,
          shopee: { ...prev.platformConfigs.shopee, isActive: false },
          tiktok: { ...prev.platformConfigs.tiktok, isActive: false }
        };
      } else {
        // user role
        updatedForm.allowedPlatforms = [];
        updatedForm.platformConfigs = {
          ...prev.platformConfigs,
          shopee: { ...prev.platformConfigs.shopee, isActive: false },
          tiktok: { ...prev.platformConfigs.tiktok, isActive: false }
        };
      }

      return updatedForm;
    });
  };

  // Handle allowed platforms change for multi-platform users
  const handleAllowedPlatformsChange = (platform) => {
    setUserForm(prev => {
      const newAllowedPlatforms = prev.allowedPlatforms.includes(platform)
        ? prev.allowedPlatforms.filter(p => p !== platform)
        : [...prev.allowedPlatforms, platform];

      return {
        ...prev,
        allowedPlatforms: newAllowedPlatforms,
        platformConfigs: {
          ...prev.platformConfigs,
          [platform]: {
            ...prev.platformConfigs[platform],
            isActive: newAllowedPlatforms.includes(platform)
          }
        }
      };
    });
  };

  const handleSubmitUser = async () => {
    try {
      if (selectedUserForEdit) {
        await updateUser(selectedUserForEdit._id, userForm);
        showNotification('Cập nhật nhân viên thành công', 'success');
      } else {
        await createUser(userForm);
        showNotification('Thêm nhân viên thành công', 'success');
      }
      handleCloseUserDialog();
      fetchData();
    } catch (error) {
      showNotification(error.message || 'Có lỗi xảy ra', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa nhân viên này?')) {
      try {
        const result = await deleteUser(userId);
        if (result.success) {
          showNotification('Xóa nhân viên thành công');
          fetchData();
        } else {
          showNotification(result.error?.message || 'Có lỗi xảy ra khi xóa nhân viên', 'error');
        }
      } catch (error) {
        showNotification('Có lỗi xảy ra khi xóa nhân viên', 'error');
      }
    }
  };

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Tổng NV', value: stats.totalUsers, color: '#2563eb', icon: PeopleIcon },
          { label: 'Đang hoạt động', value: stats.activeUsers, color: '#10b981', icon: CheckCircleIcon },
          { label: 'Tổng giờ live', value: `${stats.totalHours.toFixed(0)}h`, color: '#f59e0b', icon: AccessTimeIcon },
          { label: 'Hiệu suất TB', value: `${stats.avgPerformance}%`, color: '#8b5cf6', icon: SpeedIcon },
        ].map((s, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ borderRadius: 2, border: `1px solid ${s.color}20` }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar sx={{ bgcolor: `${s.color}15`, width: 36, height: 36 }}>
                    <s.icon sx={{ fontSize: 18, color: s.color }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                    <Typography variant="h6" fontWeight={700} color={s.color}>{s.value}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search + Filter Bar */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Tìm nhân viên..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Vai trò</InputLabel>
          <Select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} label="Vai trò" sx={{ borderRadius: 2 }}>
            <MenuItem value="">Tất cả</MenuItem>
            <MenuItem value="live_shopee">Live Shopee</MenuItem>
            <MenuItem value="live_tiktok">Live TikTok</MenuItem>
            <MenuItem value="live_multi">Multi Platform</MenuItem>
            <MenuItem value="leader_live">Leader</MenuItem>
            <MenuItem value="marketing_staff">Marketing</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">{filteredUsers.length} nhân viên</Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<SaveIcon />} onClick={handleSavePeriodSummary} disabled={savingPeriodSummary} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Lưu tổng kết kỳ
          </Button>
          <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={() => handleOpenUserDialog()} sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#2563eb' }}>
            Thêm NV
          </Button>
        </Stack>
      </Paper>

      {/* User List */}
      <Box sx={{ mb: 4 }}>
        {isMobile ? (
          /* Mobile Cards */
          <Box>
            {filteredUsers.map(user => {
              const summary = getUserSummary(user._id);
              const roleInfo = getRoleDisplayInfo(user.role, user.allowedPlatforms, user.departmentRoles);
              return (
                <Card key={user._id} sx={{ mb: 1.5, borderRadius: 2, borderLeft: `3px solid ${roleInfo.color}` }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: roleInfo.color, fontSize: 14 }}>{user.fullName?.charAt(0)}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{user.fullName}</Typography>
                        <Chip label={roleInfo.text} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${roleInfo.color}15`, color: roleInfo.color }} />
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        {(() => {
                          const c = getUserCommission(user._id);
                          return <Typography variant="body2" fontWeight={700} color="#10b981">{formatCurrency((user.baseSalary ?? 0) + (summary.totalEarnings || 0) + c.total)}</Typography>;
                        })()}
                        <Typography variant="caption" color="text.secondary">{(summary.totalHours || 0).toFixed(1)}h</Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleOpenUserDialog(user)}><Edit fontSize="small" /></IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          /* Desktop Table */
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="medium">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Nhân viên</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Vai trò</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Lương</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Giờ / KPI</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Thu nhập</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map(user => {
                  const summary = getUserSummary(user._id);
                  const kpi = Math.min(100, (summary.totalHours / (user.kpiHoursPerPeriod || 160)) * 100);
                  const roleInfo = getRoleDisplayInfo(user.role, user.allowedPlatforms, user.departmentRoles);
                  return (
                    <TableRow key={user._id} hover sx={{ borderLeft: `3px solid ${roleInfo.color}` }}>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 36, height: 36, bgcolor: roleInfo.color, fontSize: 14, fontWeight: 700 }}>
                            {user.fullName?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{user.fullName}</Typography>
                            <Typography variant="caption" color="text.secondary">@{user.username}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={roleInfo.text} size="small" sx={{ bgcolor: `${roleInfo.color}15`, color: roleInfo.color, fontWeight: 600, fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(user.baseSalary ?? 0)}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatCurrency(user.hourlyRate ?? 0)}/h</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600}>{(summary.totalHours || 0).toFixed(1)}h</Typography>
                        <LinearProgress variant="determinate" value={Math.min(100, kpi)} sx={{ height: 4, borderRadius: 2, mt: 0.5, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: kpi >= 90 ? '#10b981' : kpi >= 50 ? '#f59e0b' : '#ef4444', borderRadius: 2 } }} />
                        <Typography variant="caption" color="text.secondary">{Math.round(kpi)}% KPI</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const base = user.baseSalary ?? 0;
                          const live = summary.totalEarnings || 0;
                          const c = getUserCommission(user._id);
                          const total = base + live + c.total;
                          return (
                            <>
                              <Typography variant="body2" fontWeight={700} color="#10b981">{formatCurrency(total)}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                                {base > 0 && `CB: ${formatCurrency(base)}`}
                                {live > 0 && `${base > 0 ? ' + ' : ''}Live: ${formatCurrency(live)}`}
                                {c.commission > 0 && ` + HH: ${formatCurrency(c.commission)}`}
                                {c.leaderBonus > 0 && ` + Bonus: ${formatCurrency(c.leaderBonus)}`}
                              </Typography>
                              {c.totalGMV > 0 && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#6366f1', fontWeight: 600, fontSize: '0.65rem' }}>
                                  GMV: {formatCurrency(c.totalGMV)}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Lịch sử thu nhập"><IconButton size="small" onClick={() => handleOpenEarningsHistory(user)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Chỉnh sửa"><IconButton size="small" onClick={() => handleOpenUserDialog(user)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Xóa"><IconButton size="small" color="error" onClick={() => handleDeleteUser(user._id)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary">Không tìm thấy nhân viên</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* User Dialog with Tabs */}
      <Dialog open={openUserDialog} onClose={handleCloseUserDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', px: 3, py: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
            {selectedUserForEdit ? 'Chỉnh sửa nhân viên' : 'Thêm nhân viên mới'}
          </Typography>
        </Box>
        <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ px: 3, borderBottom: '1px solid #e2e8f0' }}>
          <Tab label="Cơ bản" />
          <Tab label="Lương & KPI" />
          <Tab label="Nâng cao" />
        </Tabs>
        <DialogContent sx={{ px: 3, py: 2.5, minHeight: 400 }}>
          {/* Tab 0: Cơ bản */}
          {dialogTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tên đăng nhập"
                  name="username"
                  value={userForm.username}
                  onChange={handleUserFormChange}
                  required
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={selectedUserForEdit ? "Mật khẩu mới (để trống nếu không đổi)" : "Mật khẩu"}
                  name="password"
                  type="password"
                  value={userForm.password}
                  onChange={handleUserFormChange}
                  required={!selectedUserForEdit}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Họ và tên đầy đủ"
                  name="fullName"
                  value={userForm.fullName}
                  onChange={handleUserFormChange}
                  required
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Vai trò trong hệ thống</InputLabel>
                  <Select
                    name="role"
                    value={userForm.role}
                    onChange={handleRoleChange}
                    label="Vai trò trong hệ thống"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="user">Nhân viên</MenuItem>
                    <MenuItem value="admin">Quản trị viên</MenuItem>
                    <MenuItem value="marketing_staff">Nhân viên Marketing</MenuItem>
                    <MenuItem value="live_shopee">Live Shopee</MenuItem>
                    <MenuItem value="live_tiktok">Live TikTok</MenuItem>
                    <MenuItem value="live_multi">Multi Platform</MenuItem>
                    <MenuItem value="leader_live">Lead Team Live</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" variant="standard">
                  <FormLabel component="legend" sx={{ fontWeight: 700, color: modernTheme.colors.primary.main }}>
                    Phòng ban
                  </FormLabel>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={userForm.departmentRoles.includes('marketing_dept')}
                          onChange={() => handleDepartmentRolesChange('marketing_dept')}
                          name="marketing_dept"
                          sx={{ color: modernTheme.colors.secondary.main, '&.Mui-checked': { color: modernTheme.colors.secondary.main } }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">Marketing</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={userForm.departmentRoles.includes('livestream_dept')}
                          onChange={() => handleDepartmentRolesChange('livestream_dept')}
                          name="livestream_dept"
                          sx={{ color: modernTheme.colors.primary.main, '&.Mui-checked': { color: modernTheme.colors.primary.main } }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">LiveStream</Typography>
                        </Box>
                      }
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Lương & KPI */}
          {dialogTab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mức lương theo giờ"
                  name="hourlyRate"
                  type="number"
                  value={userForm.hourlyRate}
                  onChange={handleUserFormChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">VNĐ/giờ</InputAdornment>,
                    inputProps: { min: 0, step: 10000 }
                  }}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mục tiêu KPI/Kỳ"
                  name="kpiHoursPerPeriod"
                  type="number"
                  value={userForm.kpiHoursPerPeriod}
                  onChange={handleUserFormChange}
                  helperText="Số giờ livestream mục tiêu trong 1 kỳ (từ ngày 10 đến ngày 9 tháng sau)"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">giờ/kỳ</InputAdornment>,
                    inputProps: { min: 0, max: 720, step: 10 }
                  }}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="% Hoa hồng (flat rate)"
                  name="commissionPercent"
                  type="number"
                  value={userForm.commissionPercent}
                  onChange={handleUserFormChange}
                  helperText="Flat rate fallback. Dùng nút bên dưới để cấu hình theo giờ."
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    inputProps: { min: 0, max: 50, step: 0.5 }
                  }}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                {selectedUserForEdit?._id && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      startIcon={<AccessTimeIcon />}
                      onClick={() => { setSelectedUserForCommission({ _id: selectedUserForEdit._id, fullName: userForm.fullName, commissionPercent: userForm.commissionPercent }); setOpenCommissionConfig(true); }}
                      sx={{ mt: 0.5, textTransform: 'none', color: '#d97706', fontWeight: 600, fontSize: '0.75rem' }}
                    >
                      Cấu hình hoa hồng theo giờ
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AccessTimeIcon />}
                      onClick={() => { setSelectedUserForPolicy({ _id: selectedUserForEdit._id, fullName: userForm.fullName, role: userForm.role }); setOpenLivePolicy(true); }}
                      sx={{ mt: 0.5, textTransform: 'none', color: '#2563eb', fontWeight: 600, fontSize: '0.75rem' }}
                    >
                      Chính sách Live (thưởng/phạt/giờ)
                    </Button>
                  </Stack>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lương cơ bản"
                  name="baseSalary"
                  type="number"
                  value={userForm.baseSalary}
                  onChange={handleUserFormChange}
                  helperText="Lương cứng hàng tháng (VNĐ)"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">VNĐ</InputAdornment>,
                    inputProps: { min: 0, step: 500000 }
                  }}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              {/* Platform configs for live_multi */}
              {userForm.role === 'live_multi' && (
                <Grid item xs={12}>
                  <FormControl component="fieldset" variant="standard">
                    <FormLabel component="legend">Cấu hình lương/giờ và KPI cho từng platform</FormLabel>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={userForm.platformConfigs.shopee.isActive}
                              onChange={(e) => setUserForm(prev => ({
                                ...prev,
                                platformConfigs: {
                                  ...prev.platformConfigs,
                                  shopee: { ...prev.platformConfigs.shopee, isActive: e.target.checked }
                                }
                              }))}
                              name="shopeeActive"
                              color="primary"
                            />
                          }
                          label="Shopee Active"
                        />
                        <TextField
                          fullWidth
                          label="Shopee Lương/Giờ"
                          name="shopeeHourlyRate"
                          type="number"
                          value={userForm.platformConfigs.shopee.hourlyRate}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            platformConfigs: {
                              ...prev.platformConfigs,
                              shopee: { ...prev.platformConfigs.shopee, hourlyRate: parseInt(e.target.value, 10) || 0 }
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">VNĐ/giờ</InputAdornment>,
                            inputProps: { min: 0, step: 10000 }
                          }}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <TextField
                          fullWidth
                          label="Shopee Mục tiêu KPI/Kỳ"
                          name="shopeeKpiHours"
                          type="number"
                          value={userForm.platformConfigs.shopee.kpiHoursPerPeriod}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            platformConfigs: {
                              ...prev.platformConfigs,
                              shopee: { ...prev.platformConfigs.shopee, kpiHoursPerPeriod: parseInt(e.target.value, 10) || 0 }
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">giờ/kỳ</InputAdornment>,
                            inputProps: { min: 0, max: 720, step: 10 }
                          }}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={userForm.platformConfigs.tiktok.isActive}
                              onChange={(e) => setUserForm(prev => ({
                                ...prev,
                                platformConfigs: {
                                  ...prev.platformConfigs,
                                  tiktok: { ...prev.platformConfigs.tiktok, isActive: e.target.checked }
                                }
                              }))}
                              name="tiktokActive"
                              color="primary"
                            />
                          }
                          label="TikTok Active"
                        />
                        <TextField
                          fullWidth
                          label="TikTok Lương/Giờ"
                          name="tiktokHourlyRate"
                          type="number"
                          value={userForm.platformConfigs.tiktok.hourlyRate}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            platformConfigs: {
                              ...prev.platformConfigs,
                              tiktok: { ...prev.platformConfigs.tiktok, hourlyRate: parseInt(e.target.value, 10) || 0 }
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">VNĐ/giờ</InputAdornment>,
                            inputProps: { min: 0, step: 10000 }
                          }}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <TextField
                          fullWidth
                          label="TikTok Mục tiêu KPI/Kỳ"
                          name="tiktokKpiHours"
                          type="number"
                          value={userForm.platformConfigs.tiktok.kpiHoursPerPeriod}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            platformConfigs: {
                              ...prev.platformConfigs,
                              tiktok: { ...prev.platformConfigs.tiktok, kpiHoursPerPeriod: parseInt(e.target.value, 10) || 0 }
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">giờ/kỳ</InputAdornment>,
                            inputProps: { min: 0, max: 720, step: 10 }
                          }}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          )}

          {/* Tab 2: Nâng cao */}
          {dialogTab === 2 && (
            <Grid container spacing={2}>
              {/* Allowed Platforms checkboxes for live_multi */}
              {userForm.role === 'live_multi' && (
                <Grid item xs={12}>
                  <FormControl component="fieldset" variant="standard">
                    <FormLabel component="legend">Platforms được phép livestream</FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={userForm.allowedPlatforms.includes('shopee')}
                            onChange={() => handleAllowedPlatformsChange('shopee')}
                            name="shopee"
                            color="primary"
                          />
                        }
                        label="Shopee"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={userForm.allowedPlatforms.includes('tiktok')}
                            onChange={() => handleAllowedPlatformsChange('tiktok')}
                            name="tiktok"
                            color="primary"
                          />
                        }
                        label="TikTok"
                      />
                    </FormGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Chọn các platform mà nhân viên được phép livestream. Nếu không chọn, họ sẽ không có quyền livestream.
                    </Typography>
                  </FormControl>
                </Grid>
              )}

              {/* Leader Config Section */}
              {userForm.role === 'leader_live' && (
                <Grid item xs={12}>
                  <FormControl component="fieldset" variant="standard" fullWidth>
                    <FormLabel component="legend" sx={{ fontWeight: 700, color: '#7c3aed', mb: 2 }}>
                      Cấu hình Lead Team Live
                    </FormLabel>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="caption">
                        Cấu hình các tỷ lệ hoa hồng team, thưởng vượt target, và danh sách thành viên team cho Lead.
                      </Typography>
                    </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="HH Team GMV (%)"
                          type="number"
                          value={userForm.leaderConfig?.teamCommissionPercent ?? 0.4}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            leaderConfig: {
                              ...prev.leaderConfig,
                              teamCommissionPercent: parseFloat(e.target.value) || 0
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            inputProps: { min: 0, max: 10, step: 0.1 }
                          }}
                          variant="outlined"
                          helperText="% tổng GMV team"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Thưởng vượt target (%)"
                          type="number"
                          value={userForm.leaderConfig?.growthBonusPercent ?? 2}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            leaderConfig: {
                              ...prev.leaderConfig,
                              growthBonusPercent: parseFloat(e.target.value) || 0
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            inputProps: { min: 0, max: 100, step: 1 }
                          }}
                          variant="outlined"
                          helperText="% GMV tăng trưởng"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="KPI TikTok Bonus (%)"
                          type="number"
                          value={userForm.leaderConfig?.tiktokKpiBonusPercent ?? 5}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            leaderConfig: {
                              ...prev.leaderConfig,
                              tiktokKpiBonusPercent: parseFloat(e.target.value) || 0
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            inputProps: { min: 0, max: 100, step: 1 }
                          }}
                          variant="outlined"
                          helperText="% TikTok GMV tăng trưởng"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Ngưỡng TikTok (%)"
                          type="number"
                          value={userForm.leaderConfig?.tiktokGrowthThreshold ?? 20}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            leaderConfig: {
                              ...prev.leaderConfig,
                              tiktokGrowthThreshold: parseFloat(e.target.value) || 0
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            inputProps: { min: 0, max: 100, step: 5 }
                          }}
                          variant="outlined"
                          helperText="TikTok GMV tăng >= X% mới tính KPI"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Giờ live tối thiểu/tháng"
                          type="number"
                          value={userForm.leaderConfig?.minLiveHoursPerMonth ?? 90}
                          onChange={(e) => setUserForm(prev => ({
                            ...prev,
                            leaderConfig: {
                              ...prev.leaderConfig,
                              minLiveHoursPerMonth: parseInt(e.target.value, 10) || 0
                            }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">giờ</InputAdornment>,
                            inputProps: { min: 0, max: 300, step: 10 }
                          }}
                          variant="outlined"
                          helperText="KPI giờ live tối thiểu"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                    </Grid>
                    {/* Team Members Selector */}
                    <Divider sx={{ my: 2 }} />
                    <FormLabel component="legend" sx={{ fontWeight: 700, color: '#7c3aed', mb: 1 }}>
                      Thành viên Team ({(userForm.leaderConfig?.teamMemberIds || []).length} người)
                    </FormLabel>
                    <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption">
                        Chọn các nhân viên live thuộc team của Lead. GMV của các thành viên được chọn sẽ dùng để tính hoa hồng team, thưởng vượt target, và KPI TikTok.
                      </Typography>
                    </Alert>
                    <Box sx={{
                      maxHeight: 250, overflow: 'auto',
                      border: '1px solid #e5e7eb', borderRadius: 2, p: 1,
                    }}>
                      {users
                        .filter(u => ['live_shopee', 'live_tiktok', 'live_multi'].includes(u.role) && u._id !== selectedUserForEdit?._id)
                        .map(u => (
                          <FormControlLabel
                            key={u._id}
                            sx={{
                              display: 'flex', width: '100%', m: 0, py: 0.5,
                              borderBottom: '1px solid #f3f4f6',
                              '&:last-child': { borderBottom: 'none' },
                              bgcolor: (userForm.leaderConfig?.teamMemberIds || []).includes(u._id) ? '#f5f3ff' : 'transparent',
                              borderRadius: 1,
                            }}
                            control={
                              <Checkbox
                                size="small"
                                checked={(userForm.leaderConfig?.teamMemberIds || []).map(String).includes(String(u._id))}
                                onChange={(e) => {
                                  const currentIds = (userForm.leaderConfig?.teamMemberIds || []).map(String);
                                  const newIds = e.target.checked
                                    ? [...currentIds, String(u._id)]
                                    : currentIds.filter(id => String(id) !== String(u._id));
                                  setUserForm(prev => ({
                                    ...prev,
                                    leaderConfig: {
                                      ...prev.leaderConfig,
                                      teamMemberIds: newIds
                                    }
                                  }));
                                }}
                                sx={{ color: '#7c3aed', '&.Mui-checked': { color: '#7c3aed' } }}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                  {u.fullName}
                                </Typography>
                                <Chip
                                  label={u.role === 'live_shopee' ? 'Shopee' : u.role === 'live_tiktok' ? 'TikTok' : 'Multi'}
                                  size="small"
                                  sx={{
                                    height: 20, fontSize: '0.6rem', fontWeight: 600,
                                    bgcolor: u.role === 'live_shopee' ? '#fee2e2' : u.role === 'live_tiktok' ? '#e0e7ff' : '#fef3c7',
                                    color: u.role === 'live_shopee' ? '#dc2626' : u.role === 'live_tiktok' ? '#4f46e5' : '#d97706',
                                  }}
                                />
                              </Box>
                            }
                          />
                        ))}
                      {users.filter(u => ['live_shopee', 'live_tiktok', 'live_multi'].includes(u.role) && u._id !== selectedUserForEdit?._id).length === 0 && (
                        <Typography variant="caption" sx={{ color: '#9ca3af', p: 1 }}>
                          Chưa có nhân viên live nào trong hệ thống.
                        </Typography>
                      )}
                    </Box>
                  </FormControl>
                </Grid>
              )}

              {/* Allowed Time Slots Section */}
              {(userForm.role === 'live_shopee' || userForm.role === 'live_tiktok' || userForm.role === 'live_multi' || userForm.role === 'leader_live') && (
                <Grid item xs={12}>
                  <FormControl component="fieldset" variant="standard" fullWidth>
                    <FormLabel component="legend" sx={{ fontWeight: 700, color: modernTheme.colors.warning.main, mb: 2 }}>
                      Giới hạn khung giờ đăng ký (Tùy chọn)
                    </FormLabel>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="caption">
                        Nếu không thiết lập, nhân viên có thể đăng ký bất kỳ khung giờ nào.
                        Nếu thiết lập, nhân viên chỉ được đăng ký các khung giờ bạn cho phép.
                      </Typography>
                    </Alert>

                    {/* Quick Select from System Time Slots */}
                    {systemTimeSlots && systemTimeSlots.length > 0 && (
                      <Card sx={{ p: 2, mb: 2, bgcolor: 'rgba(59, 130, 246, 0.05)', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Chọn nhanh từ khung giờ có sẵn
                          </Typography>
                          <Chip
                            label={`${systemTimeSlots.length} khung giờ`}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                        <Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
                          <Typography variant="caption">
                            Click vào khung giờ bên dưới để tự động điền giờ, sau đó chọn ngày và platform rồi nhấn "Thêm"
                          </Typography>
                        </Alert>
                        <Grid container spacing={1}>
                          {systemTimeSlots.map((timeSlot, index) => {
                            const [startTime, endTime] = timeSlot.split('-');
                            const isSelected = newTimeSlot.startTime === startTime && newTimeSlot.endTime === endTime;
                            return (
                              <Grid item xs={6} sm={4} md={3} key={index}>
                                <Button
                                  fullWidth
                                  variant={isSelected ? "contained" : "outlined"}
                                  size="small"
                                  onClick={() => {
                                    setNewTimeSlot(prev => ({
                                      ...prev,
                                      startTime,
                                      endTime
                                    }));
                                  }}
                                  sx={{
                                    py: 1,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    borderColor: modernTheme.colors.primary.main,
                                    bgcolor: isSelected ? modernTheme.colors.primary.main : 'transparent',
                                    color: isSelected ? 'white' : modernTheme.colors.primary.main,
                                    borderWidth: 2,
                                    borderRadius: 2,
                                    '&:hover': {
                                      bgcolor: modernTheme.colors.primary.main,
                                      color: 'white',
                                      borderColor: modernTheme.colors.primary.main,
                                      borderWidth: 2,
                                    }
                                  }}
                                >
                                  {timeSlot}
                                </Button>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Card>
                    )}

                    {/* Add New Time Slot Form */}
                    <Card sx={{ p: 2, mb: 2, bgcolor: 'rgba(245, 158, 11, 0.05)', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                        Thêm khung giờ mới
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Ngày trong tuần</InputLabel>
                            <Select
                              value={newTimeSlot.dayOfWeek}
                              label="Ngày trong tuần"
                              onChange={(e) => setNewTimeSlot(prev => ({ ...prev, dayOfWeek: e.target.value }))}
                            >
                              <MenuItem value={0}>Chủ nhật</MenuItem>
                              <MenuItem value={1}>Thứ 2</MenuItem>
                              <MenuItem value={2}>Thứ 3</MenuItem>
                              <MenuItem value={3}>Thứ 4</MenuItem>
                              <MenuItem value={4}>Thứ 5</MenuItem>
                              <MenuItem value={5}>Thứ 6</MenuItem>
                              <MenuItem value={6}>Thứ 7</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Từ giờ"
                            type="time"
                            value={newTimeSlot.startTime}
                            onChange={(e) => setNewTimeSlot(prev => ({ ...prev, startTime: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Đến giờ"
                            type="time"
                            value={newTimeSlot.endTime}
                            onChange={(e) => setNewTimeSlot(prev => ({ ...prev, endTime: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Platform</InputLabel>
                            <Select
                              value={newTimeSlot.platform}
                              label="Platform"
                              onChange={(e) => setNewTimeSlot(prev => ({ ...prev, platform: e.target.value }))}
                            >
                              <MenuItem value="both">Cả hai</MenuItem>
                              <MenuItem value="shopee">Chỉ Shopee</MenuItem>
                              <MenuItem value="tiktok">Chỉ TikTok</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={handleAddTimeSlot}
                            sx={{
                              bgcolor: modernTheme.colors.success.main,
                              height: '40px',
                              borderRadius: 2
                            }}
                          >
                            Thêm
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>

                    {/* Display Current Time Slots */}
                    {userForm.allowedTimeSlots && userForm.allowedTimeSlots.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Các khung giờ được phép ({userForm.allowedTimeSlots.length})
                        </Typography>
                        <Stack spacing={1}>
                          {userForm.allowedTimeSlots.map((slot, index) => (
                            <Card key={index} sx={{
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              bgcolor: 'rgba(16, 185, 129, 0.05)',
                              border: '1px solid rgba(16, 185, 129, 0.2)',
                              borderRadius: 2
                            }}>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip
                                  label={getDayName(slot.dayOfWeek)}
                                  size="small"
                                  sx={{
                                    bgcolor: modernTheme.colors.primary.main,
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                                <Typography variant="body2" fontWeight={600}>
                                  {slot.startTime} - {slot.endTime}
                                </Typography>
                                <Chip
                                  label={slot.platform === 'both' ? 'Cả hai' : slot.platform === 'shopee' ? 'Shopee' : 'TikTok'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 500 }}
                                />
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveTimeSlot(index)}
                                sx={{
                                  color: modernTheme.colors.error.main,
                                  '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.1)' }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {(!userForm.allowedTimeSlots || userForm.allowedTimeSlots.length === 0) && (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        <Typography variant="caption">
                          Chưa có giới hạn khung giờ. Nhân viên có thể đăng ký bất kỳ khung giờ nào.
                        </Typography>
                      </Alert>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* Show message if no advanced options for current role */}
              {userForm.role !== 'live_multi' && userForm.role !== 'leader_live' && userForm.role !== 'live_shopee' && userForm.role !== 'live_tiktok' && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      Không có tùy chọn nâng cao cho vai trò này.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseUserDialog} sx={{ borderRadius: 2 }}>Hủy</Button>
          <Button variant="contained" onClick={handleSubmitUser} sx={{ borderRadius: 2, bgcolor: '#2563eb' }}>
            {selectedUserForEdit ? 'Cập nhật' : 'Tạo mới'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Period Dialog */}
      <Dialog
        open={openPeriodDialog}
        onClose={() => {setOpenPeriodDialog(false); setDialogSlide(false);}}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{
          direction: 'up',
          in: dialogSlide
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: modernTheme.shadows.elevated
          }
        }}
      >
        <DialogTitle sx={{
          background: modernTheme.colors.success.gradient,
          color: 'white',
          p: 3
        }}>
          <Typography variant="h5" fontWeight={700}>
            Lưu tổng kết thu nhập theo kỳ
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            Chọn loại kỳ bạn muốn lưu tổng kết
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
            <Typography variant="body2">
              Hệ thống sẽ tự động tính toán và lưu tổng kết thu nhập cho tất cả nhân viên
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 3,
                  border: '2px solid',
                  borderColor: modernTheme.colors.success.main,
                  cursor: 'pointer',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: modernTheme.shadows.elevated,
                    background: 'rgba(22, 163, 74, 0.03)'
                  }
                }}
                onClick={() => handleConfirmSavePeriodSummary('completed')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    background: modernTheme.colors.success.gradient,
                    borderRadius: '50%',
                    p: 1.5,
                    mr: 3
                  }}>
                    <CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                      Kỳ vừa kết thúc
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Lưu tổng kết kỳ đã hoàn thành (từ ngày 10 tháng trước đến ngày 9)
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card
                sx={{
                  p: 3,
                  border: '2px solid',
                  borderColor: modernTheme.colors.info.main,
                  cursor: 'pointer',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: modernTheme.shadows.elevated,
                    background: 'rgba(8, 145, 178, 0.03)'
                  }
                }}
                onClick={() => handleConfirmSavePeriodSummary('current')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    background: modernTheme.colors.info.gradient,
                    borderRadius: '50%',
                    p: 1.5,
                    mr: 3
                  }}>
                    <AccessTimeIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                      Kỳ hiện tại (đang diễn ra)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Lưu tổng kết kỳ đang diễn ra (có thể chưa kết thúc hoàn toàn)
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 4 }}>
          <Button
            onClick={() => {setOpenPeriodDialog(false); setDialogSlide(false);}}
            variant="outlined"
            size="large"
            sx={{ borderRadius: 2 }}
          >
            Hủy bỏ
          </Button>
        </DialogActions>
      </Dialog>

      {/* UserEarningsHistory Dialog */}
      {selectedUserForHistory && (
        <UserEarningsHistory
          open={openEarningsHistory}
          onClose={handleCloseEarningsHistory}
          userId={selectedUserForHistory._id}
          userName={selectedUserForHistory.fullName || selectedUserForHistory.username}
          showNotification={showNotification}
          isMobile={isMobile}
        />
      )}

      {/* Commission Config Editor Dialog */}
      <CommissionConfigEditor
        open={openCommissionConfig}
        onClose={() => { setOpenCommissionConfig(false); setSelectedUserForCommission(null); }}
        user={selectedUserForCommission}
        onSaved={() => {}}
      />

      {/* Live Policy Editor Dialog */}
      <LivePolicyEditor
        open={openLivePolicy}
        onClose={() => { setOpenLivePolicy(false); setSelectedUserForPolicy(null); }}
        user={selectedUserForPolicy}
        onSaved={() => {}}
      />
    </Box>
  );
};

export default UserManagement;
